/**
 * Custom ListItem extension with NodeView for TipTap.
 * Adds nodeId and locked attributes.
 */

import { useCallback } from "react";
import ListItem from "@tiptap/extension-list-item";
import { NodeViewWrapper, NodeViewContent, ReactNodeViewRenderer } from "@tiptap/react";
import type { ReactNodeViewProps } from "@tiptap/react";

function ListItemComponent({ node, editor, getPos }: ReactNodeViewProps) {
  const locked = node.attrs.locked || false;

  // Handle clicks on empty space - put cursor at end of text
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      // If clicking inside content, let it handle naturally
      if (target.closest("[data-node-view-content]")) return;

      const pos = getPos();
      if (pos === undefined) return;

      // Clicked on empty space - set cursor to end of this node's content
      e.preventDefault();
      editor.view.focus();
      const end = pos + node.nodeSize - 1;
      editor.commands.setTextSelection(end);
    },
    [editor, getPos, node.nodeSize]
  );

  return (
    <NodeViewWrapper
      as="li"
      className={`list-item-wrapper ${locked ? "locked" : ""}`}
      onMouseDown={handleMouseDown}
    >
      <NodeViewContent />
    </NodeViewWrapper>
  );
}

const CustomListItem = ListItem.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      nodeId: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-node-id"),
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes.nodeId) return {};
          return { "data-node-id": attributes.nodeId };
        },
      },
      locked: {
        default: false,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-locked") === "true",
        renderHTML: (attributes: Record<string, unknown>) => {
          if (!attributes.locked) return {};
          return { "data-locked": "true" };
        },
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ListItemComponent);
  },
});

export default CustomListItem;
